/**
 * UserTopBar — v1.8 shared top bar for user-facing pages.
 *
 * Compact header used by /perfil, /perfil/*, /creditos and /aplicaciones.
 * Exposes CreditsChip (also reused by WorkspaceUser).
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Coins } from 'lucide-react';
import { useCredits } from '../../contexts/CreditsContext';
import { useT } from '../../contexts/LocaleContext';
import { AdminAvatarMenu } from '../shell/AdminAvatarMenu';

export const CreditsChip: React.FC<{ balance?: number | null; onClick?: () => void }> = ({ balance, onClick }) => {
  const t = useT();
  const [pulse, setPulse] = useState(false);
  const prev = useRef<number | null>(null);
  useEffect(() => {
    if (balance != null && prev.current != null && balance !== prev.current) {
      setPulse(true);
      const tid = setTimeout(() => setPulse(false), 900);
      prev.current = balance;
      return () => clearTimeout(tid);
    }
    prev.current = balance ?? null;
  }, [balance]);
  // Sprint Estabilización Final · Feb 2026 · muestra sobregiro real
  // (balance puede ser negativo hasta -7). Math.floor conserva el
  // signo negativo — no clampear a 0.
  const display = balance == null ? '—' : Math.floor(balance).toString();
  const isNegative = typeof balance === 'number' && balance < 0;
  return (
    <button
      onClick={onClick}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition"
      style={{
        background: isNegative ? 'hsl(0 84% 65% / 0.12)' : 'hsl(var(--accent) / 0.12)',
        border: `1px solid ${isNegative ? 'hsl(0 84% 65% / 0.6)' : 'hsl(var(--accent) / ' + (pulse ? 0.7 : 0.32) + ')'}`,
        color: isNegative ? 'hsl(0 84% 65%)' : 'hsl(var(--accent))',
        boxShadow: pulse ? '0 0 16px hsl(var(--accent) / 0.45)' : 'none',
      }}
      data-testid="topbar-credits-chip"
      title={t('credits.balance')}
      aria-label={t('credits.balance')}
    >
      <Coins className="w-3.5 h-3.5" />
      <span>{t('credits.label')} {display}</span>
    </button>
  );
};

interface Props {
  title: string;
  onBack?: () => void;
  showCredits?: boolean;
  avatarUrl?: string | null;
  right?: React.ReactNode;
}

export const UserTopBar: React.FC<Props> = ({ title, onBack, showCredits = true, avatarUrl, right }) => {
  const nav = useNavigate();
  const { balance } = useCredits();
  return (
    <header
      className="sticky top-0 z-20 backdrop-blur-md"
      style={{
        background: 'linear-gradient(180deg, hsl(var(--bg-elev) / 0.85), hsl(var(--bg-elev) / 0.4))',
        borderBottom: '1px solid hsl(var(--border))',
      }}
    >
      <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
        <button
          onClick={onBack || (() => nav('/home'))}
          className="inline-flex items-center gap-1.5 text-sm"
          style={{ color: 'hsl(var(--text-muted))' }}
          data-testid="topbar-back"
        >
          <ArrowLeft size={16} /> Volver
        </button>
        <div className="text-xs tracking-[0.24em] uppercase font-semibold" style={{ color: 'hsl(var(--accent))' }}>
          {title}
        </div>
        <div className="flex items-center gap-2">
          {right}
          {showCredits && <CreditsChip balance={balance} onClick={() => nav('/creditos')} />}
          <div data-testid="topbar-avatar"><AdminAvatarMenu /></div>
        </div>
      </div>
    </header>
  );
};
